import { Component, inject, Input, OnChanges, OnInit } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { DeleteButtonComponent } from "@components/delete-button/delete-button.component";
import { InvitePersonToProjectModalComponent } from "@components/invite-person-to-project-modal/invite-person-to-project-modal.component";
import { ProjectApiService } from "@domain/project/api/project-api.service";
import { Collaborator } from "@domain/project/collaborator";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { IconComponent } from "@odx/angular/components/icon";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import { TableCellDirective, TableComponent, TableHeaderCell, TableRowDirective } from "@odx/angular/components/table";
import { ReadAccessModalService } from "@utils/modals/read-access-modal.service";
import { ToastHelper } from "@utils/toast-helper.service";
import { catchError, EMPTY, filter, map, Observable, switchMap, tap } from "rxjs";
import { LocalizeDatePipe } from "src/locale/localize-date";
import { LocalizeTimePipe } from "src/locale/localize-time";

@Component({
  selector: "app-project-collaboration",
  imports: [
    TableRowDirective,
    TableComponent,
    TableCellDirective,
    IconComponent,
    UiKitModule,
    LocalizeDatePipe,
    LocalizeTimePipe,
    DeleteButtonComponent,
  ],
  templateUrl: "./project-collaboration.component.html",
  styleUrl: "./project-collaboration.component.scss",
  standalone: true,
})
export class ProjectCollaborationComponent implements OnInit, OnChanges {
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private toastHelper = inject(ToastHelper);
  private apiService = inject(ProjectApiService);
  private readAccessModalService = inject(ReadAccessModalService);

  @Input() set projectData(project: ScopedProject) {
    this.project = project;
  }

  @Input() isHidden!: boolean;

  protected project!: ScopedProject;
  protected collaborators: Collaborator[] = [];
  protected projectOwner!: Collaborator;
  protected readonly navigator = navigator;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;

  protected sharedProjectLink: string | undefined;
  protected headerData: TableHeaderCell[] = [
    { name: "mail", title: $localize`:@@collaborationTable.column1.header:E-Mail-Adresse` },
    { name: "invitedBy", title: $localize`:@@collaborationTable.column2.header:Eingeladen von` },
    { name: "invitationCompletetAt", title: $localize`:@@collaborationTable.column3.header:Hinzugefügt am` },
    { name: "delete", title: "" },
  ];

  private readonly modalService = inject(ModalService);

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
  }

  ngOnInit(): void {
    this.sharedProjectLink = this.project.cloudId ? this.projectService.getSharedProjectLink(this.project.cloudId) : undefined;
    this.getCollaborators();
  }

  get offlineToolTipMessage() {
    return !navigator.onLine
      ? $localize`:@@collaboration.removeCollaborator:Diese Funktion ist nur im Online-Modus verfügbar.`
      : "";
  }

  ngOnChanges() {
    if (this.project.cloudId) {
      this.getCollaborators();
    }
  }

  getCollaborators() {
    if (this.project.cloudId)
      this.projectService.loadProjectUsers(this.project.cloudId).subscribe((collaborators) => {
        this.collaborators = collaborators;
        this.filterCollaboratorTable();
      });
  }

  filterCollaboratorTable() {
    this.collaborators.sort((a, b) => a.email.localeCompare(b.email));
    this.projectOwner = this.collaborators.find((user) => user.assignedRole === "OWNER")!;
    this.collaborators = this.collaborators.filter((user) => user.assignedRole !== "OWNER");
  }

  deleteCollaborator(collaboratorId: string, collaboratorEmail: string) {
    this.modalService
      .open(ConfirmModalComponent, {
        id: "collab-confirm-modal",
        data: {
          text: $localize`:@@modal.deleteCollaborator:Wollen Sie den Kollaborator ${collaboratorEmail} wirklich löschen?`,
        },
      })
      .onClose$.pipe(
        switchMap(() => this.hasWritePermission$),
        filter((hasPermission): hasPermission is boolean => hasPermission !== undefined),
        switchMap(() => this.projectService.removeCollaboratorFromProject(this.project.cloudId, collaboratorId)),
        tap(() => this.getCollaborators()),
        catchError((error) => {
          console.error("Error:", error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  openReadAccessModal() {
    this.readAccessModalService.openModal(this.project);
  }

  protected openModal() {
    this.modalService
      .open(InvitePersonToProjectModalComponent, {
        size: ModalSize.MEDIUM,
      })
      .onClose$.pipe(
        filter((value) => !!value && !!this.project?.cloudId),
        switchMap((value) => this.apiService.inviteCollaborators(value, this.project.cloudId)),
      )
      .subscribe((response) => {
        const errors = response
          .filter((res: { success: boolean; message: string; to?: string }) => {
            return !res.success;
          })
          .map((res: { success: boolean; message: string; to?: string }) => res.to);

        if (errors.length >= 1) {
          this.toastHelper.error(
            $localize`:@@toast.invitation.error:Einladung konnte nicht verschickt werden an: ` + errors.join(", "),
          );
        } else {
          this.toastHelper.success($localize`:@@toast.invitation.successful:Einladung wurde erfolgreich verschickt`);
        }
      });
  }
}
